/* Draft-only adapter: complete native category/filter membership + real model rank.
 * No admin/API credentials, new shared filters, or collection order mutations. */
(function (root) {
  'use strict';
  const version = 'category-preview-model-units-v1';
  const handlePattern = /^[a-z0-9][a-z0-9-]{0,254}$/;
  const idPattern = /^[1-9]\d{0,19}$/;
  const cache = new Map();

  function collectionPath(url) {
    const match = url.pathname.match(/^(\/(?:[a-z]{2}(?:-[A-Za-z]{2})?))?\/collections\/([a-z0-9][a-z0-9-]*)(\/.*)?$/);
    return match && !match[3]?.startsWith('/products/') ? { locale: match[1] || '', handle: match[2] } : null;
  }
  function valid(config, pageUrl) {
    const page = collectionPath(new URL(pageUrl));
    const index = config?.preview;
    return Boolean(page && page.handle === config?.source && index?.version === version && index.themeId === '185985237370' &&
      typeof index.runId === 'string' && Array.isArray(index.sources) && index.sources.includes(page.handle) &&
      Array.isArray(index.ids) && index.ids.length > 0 && index.ids.length <= 5000 && index.ids.every(id => typeof id === 'string' && idPattern.test(id)) &&
      new Set(index.ids).size === index.ids.length);
  }
  function handles(value, pageUrl, config) {
    const page = new URL(pageUrl);
    const url = new URL(value, page);
    return valid(config, pageUrl) && url.origin === page.origin && collectionPath(url)?.handle === config.source &&
      url.searchParams.get('sort_by') === 'best-selling';
  }
  function rankedPage(products, index, page, pageSize) {
    if (!Number.isInteger(page) || page < 1 || !Number.isInteger(pageSize) || pageSize < 1 || pageSize > 250) throw new Error('Invalid category page');
    const ids = new Set();
    for (const product of products) {
      if (!idPattern.test(product.id) || !handlePattern.test(product.handle) || ids.has(product.id)) throw new Error('Incomplete or duplicate category membership');
      ids.add(product.id);
    }
    const ranks = new Map(index.ids.map((id, i) => [id, i]));
    // New products since the daily run stay present, after ranked products.
    return products.map((product, i) => ({ ...product, rank: ranks.get(product.id) ?? index.ids.length + i }))
      .sort((a, b) => a.rank - b.rank).slice((page - 1) * pageSize, page * pageSize);
  }
  function membersUrl(customer, runId, page = 1, sort = 'title-ascending') {
    const url = new URL(customer);
    ['section_id', 'sections'].forEach(key => url.searchParams.delete(key));
    url.searchParams.set('view', 'spike-category-members');
    // Stable native order independent of the daily ST/manual collection reorder.
    if (sort) url.searchParams.set('sort_by', sort);
    else url.searchParams.delete('sort_by');
    url.searchParams.set('page', String(page));
    url.searchParams.set('_spike_rank', runId);
    return url;
  }
  function validateMembers(data, source, count, page) {
    if (data?.version !== 1 || data.handle !== source || data.count !== count || data.page !== page ||
      data.pageSize !== 250 || data.pages !== Math.ceil(count / 250) || !Array.isArray(data.products) ||
      data.products.length !== Math.min(250, count - (page - 1) * 250)) throw new Error('Native category membership changed or was incomplete');
    return data.products;
  }
  async function parallel(items, operation, concurrency = 3) {
    const results = new Array(items.length);
    let next = 0, failure;
    await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, async () => {
      while (!failure && next < items.length) {
        const i = next++;
        try { results[i] = await operation(items[i], i); } catch (error) { failure ??= error; }
      }
    }));
    if (failure) throw failure;
    return results;
  }
  const parse = html => new DOMParser().parseFromString(html, 'text/html');
  async function read(url, options) {
    const response = await root.fetch(url, { ...options, credentials: 'same-origin' });
    if (!response.ok) throw new Error(`Category preview request failed (${response.status})`);
    return response.text();
  }
  async function members(customer, config, count, options, sort = 'title-ascending') {
    const runId = config.preview?.runId || 'sale';
    const url = membersUrl(customer, runId, 1, sort);
    url.searchParams.sort();
    const key = url.href;
    const previous = cache.get(key);
    if (previous && (count == null || previous.count === count) && Date.now() - previous.at < 60000) return previous.products;
    const first = JSON.parse(await read(url, options));
    count ??= first.count;
    if (!Number.isInteger(count) || count < 0 || count > 5000) throw new Error('Invalid collection size');
    const products = validateMembers(first, config.source, count, 1);
    const rest = await parallel(Array.from({ length: Math.max(0, first.pages - 1) }, (_, i) => i + 2), async page => {
      const data = JSON.parse(await read(membersUrl(customer, runId, page, sort), options));
      return validateMembers(data, config.source, count, page);
    });
    const all = products.concat(...rest);
    if (all.length !== count || all.some(p => !idPattern.test(p.id) || !handlePattern.test(p.handle)) || new Set(all.map(p => p.id)).size !== count) throw new Error('Category pages contain missing or duplicate products');
    if (cache.size >= 6) cache.delete(cache.keys().next().value);
    cache.set(key, { count, products: all, at: Date.now() });
    return all;
  }
  async function cards(customer, config, products, meta, grid, options) {
    const path = collectionPath(customer);
    const sectionId = grid.dataset.id;
    if (!/^[a-zA-Z0-9_-]{1,100}$/.test(sectionId || '')) throw new Error('Invalid product grid section');
    const layout = grid.classList.contains('productList') ? '1' : grid.className.match(/\bcolumn-(\d+)\b/)?.[1] || '3';
    const chunks = [];
    for (let i = 0; i < products.length; i += 16) chunks.push({ offset: i, products: products.slice(i, i + 16) });
    const html = await parallel(chunks, async chunk => {
      const url = new URL(`${path.locale}/search`, customer);
      url.searchParams.set('view', 'spike-category-cards');
      url.searchParams.set('type', 'product');
      url.searchParams.set('q', ['spike-cards-v1', config.source, sectionId, layout,
        (meta.page - 1) * meta.pageSize + chunk.offset, chunk.products.map(p => p.handle).join(',')].join('|'));
      for (const key of ['preview_theme_id', '_ab', '_fd', '_sc']) {
        if (customer.searchParams.has(key)) url.searchParams.set(key, customer.searchParams.get(key));
      }
      const doc = parse(await read(url, options));
      const wrapper = doc.querySelector('[data-spike-preview-cards]');
      const nodes = wrapper ? [...wrapper.children] : [];
      if (wrapper?.dataset.source !== config.source || nodes.length !== chunk.products.length ||
        nodes.some((node, i) => !node.matches('li.product') || node.dataset.spikeProductId !== chunk.products[i].id)) {
        throw new Error('Product cards did not match the ranked category page');
      }
      return nodes.map(node => node.outerHTML).join('');
    });
    return html.join('');
  }
  async function load(value, html, config, options = {}) {
    const customer = new URL(value);
    if (!handles(customer, root.location.href, config)) throw new Error('Category preview does not match the current page');
    const doc = parse(html);
    let meta;
    try { meta = JSON.parse(doc.querySelector('[data-spike-category-page]')?.textContent); } catch { /* checked below */ }
    if (meta?.handle !== config.source || !Number.isInteger(meta.count) || meta.count < 0 || meta.count > 5000 ||
      meta.page !== Number(customer.searchParams.get('page') || 1)) throw new Error('Invalid original category response');
    if (!meta.count) return html;
    const grid = doc.querySelector('#main-collection-product-grid.productListing');
    if (!grid) throw new Error('Category grid is missing');
    const products = await members(customer, config, meta.count, options);
    const selected = rankedPage(products, config.preview, meta.page, meta.pageSize);
    const expectedCount = Math.max(0, Math.min(meta.pageSize, meta.count - (meta.page - 1) * meta.pageSize));
    if (selected.length !== expectedCount) throw new Error('Category preview page is incomplete');
    grid.innerHTML = await cards(customer, config, selected, meta, grid, options);
    grid.dataset.spikeRanking = 'preview';
    grid.dataset.spikeRankingRun = config.preview.runId;
    return doc.documentElement.outerHTML;
  }
  const api = { valid, handles, rankedPage, membersUrl, validateMembers, members, cards, collectionPath, load };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (root?.document) root.SpikeCategoryPreview = api;
})(typeof window === 'undefined' ? null : window);
